import React from "react";
import useSWRInfinite from "swr/infinite";
import { DateTime } from "luxon";
import { list } from "../api/api";
import { ContactTable } from "../components/ContactTable";
import { Upload } from "../components/Upload";
import { useLocalStorage } from "../common/localStorage";
import ExpandableSection from "@cloudscape-design/components/expandable-section";
import config from "../config";
import { payloadFromToken } from "../api/auth";
import { PRIVILEGED_USERS } from "../constants/auth";

import { useDangerAlert } from "../hooks/useAlert";
import {
  Button,
  ContentLayout,
  Header,
  Grid,
  Container,
  DateRangePicker,
  SpaceBetween,
  Input,
  Select,
} from "@cloudscape-design/components";

const CHICAGO_TZ = "America/Chicago";

const buildYesterdayRange = () => {
  const chicagoNow = DateTime.now().setZone(CHICAGO_TZ);
  const start = chicagoNow.minus({ days: 1 }).startOf("day");
  const end = chicagoNow.minus({ days: 1 }).endOf("day");

  return {
    type: "absolute",
    startDate: start.toISODate(),
    endDate: end.toISODate(),
  };
};

const normalizeDateOnly = (value) => {
  if (!value) return null;

  const datePortion = value.trim().split(/[T\s]/)[0]?.replace(/\//g, "-");
  if (!datePortion) return null;

  const dt = DateTime.fromISO(datePortion, { zone: CHICAGO_TZ });
  if (!dt.isValid) return null;
  return dt.toISODate();
};

export const parseDurationToSeconds = (value) => {
  if (!value) return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const parts = trimmed.split(":");
  if (parts.length !== 2) return null;
  const [minutesText, secondsText] = parts;
  if (!minutesText || secondsText === undefined) return null;

  const minutes = Number(minutesText);
  const seconds = Number(secondsText);
  if (Number.isNaN(minutes) || Number.isNaN(seconds)) return null;
  if (minutes < 0 || seconds < 0 || seconds >= 60) return null;

  return minutes * 60 + seconds;
};

const toUtcIsoBounds = (range) => {
  const startDate = normalizeDateOnly(range?.startDate);
  const endDateRaw = normalizeDateOnly(range?.endDate ?? range?.startDate);

  if (!startDate) return { since: null, until: null };

  const endDate = endDateRaw || startDate;

  const start = DateTime.fromISO(startDate, { zone: CHICAGO_TZ });
  const end = DateTime.fromISO(endDate, { zone: CHICAGO_TZ });

  if (!start.isValid || !end.isValid) return { since: null, until: null };

  return {
    since: start.startOf("day").toUTC().toISO(),
    until: end.endOf("day").toUTC().toISO(),
  };
};

const validateRange = (range) => {
  if (!range?.startDate || !(range?.endDate ?? range?.startDate)) {
    return { valid: false, errorMessage: "Select a start and end date" };
  }

  const startDate = normalizeDateOnly(range.startDate);
  const endDate = normalizeDateOnly(range.endDate ?? range.startDate);

  if (!startDate || !endDate) {
    return { valid: false, errorMessage: "Invalid date" };
  }

  const start = DateTime.fromISO(startDate, { zone: CHICAGO_TZ });
  const end = DateTime.fromISO(endDate, { zone: CHICAGO_TZ });

  if (!start.isValid || !end.isValid) {
    return { valid: false, errorMessage: "Invalid date" };
  }

  if (start > end) {
    return { valid: false, errorMessage: "The end date must be on or after the start date" };
  }

  return { valid: true };
};

const FILTER_STORAGE_KEY = "home-filters";
const buildDefaultFilters = () => {
  const yesterdayRange = buildYesterdayRange();

  return {
    dateRange: yesterdayRange,
    activeDateRange: yesterdayRange,
    agentFilterInput: "",
    agentNameFilterInput: "",
    customerFilterInput: "",
    stateFilterInput: "",
    summaryFilterInput: "",
    topicFilterInput: "",
    scoreFilterInput: "",
    durationMinFilterInput: "",
    durationMaxFilterInput: "",
    activeAgentFilter: "",
    activeAgentNameFilter: "",
    activeCustomerFilter: "",
    activeStateFilter: "",
    activeSummaryFilter: "",
    activeTopicFilter: "",
    activeScoreFilter: null,
    activeDurationMinFilter: null,
    activeDurationMaxFilter: null,
  };
};

function Home({ setAlert }) {
  const filterControlWidth = "12rem";
  const topicOptions = React.useMemo(
    () => [
      { label: "ALL", value: "" },
      { label: "Sales", value: "Sales" },
      { label: "Customer Service", value: "Customer Service" },
      { label: "Internal", value: "Internal" },
      { label: "Personal", value: "Personal" },
    ],
    []
  );
  const scoreOptions = React.useMemo(
    () => [
      { label: "ALL", value: "" },
      ...Array.from({ length: 10 }, (_, index) => {
        const value = String(index + 1);
        return { label: value, value };
      }),
    ],
    []
  );
  const defaultFilters = React.useMemo(buildDefaultFilters, []);
  const [storedFilters, setStoredFilters] = useLocalStorage(FILTER_STORAGE_KEY, defaultFilters);
  const [username, setUsername] = React.useState(() => config?.auth?.username || "");
  const [dateRange, setDateRange] = React.useState(
    storedFilters?.dateRange ?? defaultFilters.dateRange
  );
  const [activeDateRange, setActiveDateRange] = React.useState(
    storedFilters?.activeDateRange ?? storedFilters?.dateRange ?? defaultFilters.activeDateRange
  );
  const [agentFilterInput, setAgentFilterInput] = React.useState(
    storedFilters?.agentFilterInput ?? defaultFilters.agentFilterInput
  );
  const [agentNameFilterInput, setAgentNameFilterInput] = React.useState(
    storedFilters?.agentNameFilterInput ?? defaultFilters.agentNameFilterInput
  );
  const [customerFilterInput, setCustomerFilterInput] = React.useState(
    storedFilters?.customerFilterInput ?? defaultFilters.customerFilterInput
  );
  const [stateFilterInput, setStateFilterInput] = React.useState(
    storedFilters?.stateFilterInput ?? defaultFilters.stateFilterInput
  );
  const [summaryFilterInput, setSummaryFilterInput] = React.useState(
    storedFilters?.summaryFilterInput ?? defaultFilters.summaryFilterInput
  );
  const [topicFilterInput, setTopicFilterInput] = React.useState(
    storedFilters?.topicFilterInput ?? defaultFilters.topicFilterInput
  );
  const [scoreFilterInput, setScoreFilterInput] = React.useState(
    storedFilters?.scoreFilterInput ?? defaultFilters.scoreFilterInput
  );
  const [durationMinFilterInput, setDurationMinFilterInput] = React.useState(
    storedFilters?.durationMinFilterInput ?? defaultFilters.durationMinFilterInput
  );
  const [durationMaxFilterInput, setDurationMaxFilterInput] = React.useState(
    storedFilters?.durationMaxFilterInput ?? defaultFilters.durationMaxFilterInput
  );
  const [activeAgentFilter, setActiveAgentFilter] = React.useState(
    storedFilters?.activeAgentFilter ?? defaultFilters.activeAgentFilter
  );
  const [activeAgentNameFilter, setActiveAgentNameFilter] = React.useState(
    storedFilters?.activeAgentNameFilter ?? defaultFilters.activeAgentNameFilter
  );
  const [activeCustomerFilter, setActiveCustomerFilter] = React.useState(
    storedFilters?.activeCustomerFilter ?? defaultFilters.activeCustomerFilter
  );
  const [activeStateFilter, setActiveStateFilter] = React.useState(
    storedFilters?.activeStateFilter ?? defaultFilters.activeStateFilter
  );
  const [activeSummaryFilter, setActiveSummaryFilter] = React.useState(
    storedFilters?.activeSummaryFilter ?? defaultFilters.activeSummaryFilter
  );
  const [activeTopicFilter, setActiveTopicFilter] = React.useState(
    storedFilters?.activeTopicFilter ?? defaultFilters.activeTopicFilter
  );
  const [activeScoreFilter, setActiveScoreFilter] = React.useState(
    storedFilters?.activeScoreFilter ?? defaultFilters.activeScoreFilter
  );
  const [activeDurationMinFilter, setActiveDurationMinFilter] = React.useState(
    storedFilters?.activeDurationMinFilter ?? defaultFilters.activeDurationMinFilter
  );
  const [activeDurationMaxFilter, setActiveDurationMaxFilter] = React.useState(
    storedFilters?.activeDurationMaxFilter ?? defaultFilters.activeDurationMaxFilter
  );

  const { since, until } = React.useMemo(
    () => toUtcIsoBounds(activeDateRange),
    [activeDateRange]
  );
  const displayStart = React.useMemo(
    () => normalizeDateOnly(activeDateRange?.startDate) || "",
    [activeDateRange?.startDate]
  );
  const displayEnd = React.useMemo(
    () => normalizeDateOnly(activeDateRange?.endDate ?? activeDateRange?.startDate) || "",
    [activeDateRange?.endDate, activeDateRange?.startDate]
  );

  const fetcher = (url, startKey, sinceParam, untilParam) => {
    const opts = {
      count: config.api.pageSize,
      since: sinceParam,
      until: untilParam,
    };

    if (startKey) opts.startKey = startKey;
    return list(opts);
  };

  const getKey = (pageIndex, previousPageData) => {
    if (pageIndex > 0 && !previousPageData) return null;
    if (previousPageData && !previousPageData.StartKey) return null;

    const startKey = pageIndex === 0 ? null : previousPageData?.StartKey;

    return ["/list", startKey, since, until];
  };

  const { data, error, size, setSize, mutate } = useSWRInfinite(getKey, fetcher);

  React.useEffect(() => {
    const storedToken =
      window.localStorage.getItem("id_token") ||
      window.localStorage.getItem("access_token");

    if (!storedToken) return;

    try {
      const payload = payloadFromToken(storedToken);
      const derivedUsername =
        payload?.["cognito:username"] || payload?.username || payload?.email;
      if (derivedUsername) {
        setUsername(derivedUsername);
      }
    } catch (err) {
      console.debug("Unable to derive username from token", err);
    }
  }, []);

  const isLoadingInitialData = !data && !error;
  const pages = (data || []).filter(Boolean);
  const loadedPages = pages.length;
  const isLoadingMore = isLoadingInitialData || loadedPages < size;
  const aggregatedRecords = React.useMemo(
    () => pages.flatMap((page) => page?.Records ?? []),
    [pages]
  );
  const filteredRecords = React.useMemo(() => {
    const agent = activeAgentFilter.trim().toLowerCase();
    const agentName = activeAgentNameFilter.trim().toLowerCase();
    const customer = activeCustomerFilter.trim().toLowerCase();
    const state = activeStateFilter.trim().toLowerCase();
    const summary = activeSummaryFilter.trim().toLowerCase();
    const topic = activeTopicFilter.trim().toLowerCase();
    const score = activeScoreFilter ?? null;
    const durationMin = activeDurationMinFilter;
    const durationMax = activeDurationMaxFilter;

    const restrictedExtensions = ["7026", "7014", "7011", "7079"];
    const isPrivileged = PRIVILEGED_USERS.includes(username);

    return aggregatedRecords.filter((record) => {
      if (!isPrivileged && (restrictedExtensions.includes(record.agent) || restrictedExtensions.includes(record.customer))) return false;

      if (agent && !(record.agent ?? "").toLowerCase().includes(agent)) return false;
      if (agentName && !(record.employee ?? "").toLowerCase().includes(agentName)) return false;
      if (customer && !(record.customer ?? "").toLowerCase().includes(customer)) return false;
      if (state && !(record.state ?? "").toLowerCase().includes(state)) return false;
      if (summary && !(record.summary_summary ?? "").toLowerCase().includes(summary)) return false;
      if (topic && (record.summary_topic ?? "").toLowerCase() !== topic) return false;
      if (score !== null && score !== "") {
        const recordScore = Number(record.qualityScore);
        if (Number.isNaN(recordScore) || recordScore !== score) return false;
      }
      if (
        (durationMin !== null && durationMin !== undefined) ||
        (durationMax !== null && durationMax !== undefined)
      ) {
        const recordDuration = Number(record.duration);
        if (Number.isNaN(recordDuration)) return false;
        if (durationMin !== null && durationMin !== undefined && recordDuration < durationMin) {
          return false;
        }
        if (durationMax !== null && durationMax !== undefined && recordDuration > durationMax) {
          return false;
        }
      }
      return true;
    });
  }, [
    activeAgentFilter,
    activeAgentNameFilter,
    activeCustomerFilter,
    activeStateFilter,
    activeSummaryFilter,
    activeTopicFilter,
    activeScoreFilter,
    activeDurationMinFilter,
    activeDurationMaxFilter,
    aggregatedRecords,
    username,
  ]);
  const lastPage = loadedPages ? pages[loadedPages - 1] : null;
  const lastRecords = lastPage?.Records ?? [];
  const isReachingEnd =
    loadedPages > 0 &&
    (!lastPage?.StartKey || lastRecords.length < config.api.pageSize);
  const isFetchingAdditionalPages =
    loadedPages > 0 && !isReachingEnd && isLoadingMore;
  const isTableLoading = isLoadingInitialData || isFetchingAdditionalPages;

  useDangerAlert(error, setAlert);

  React.useEffect(() => {
    if (!isLoadingInitialData && !isReachingEnd && !isLoadingMore) {
      setSize((prevSize) => prevSize + 1);
    }
  }, [isLoadingInitialData, isLoadingMore, isReachingEnd, setSize]);

  React.useEffect(() => {
    setSize(1);
  }, [since, until, setSize]);

  const handleResetDateRange = () => {
    const resetRange = buildYesterdayRange();
    setDateRange(resetRange);
    setActiveDateRange(resetRange);
    setStoredFilters((previous) => ({
      ...(previous || defaultFilters),
      dateRange: resetRange,
      activeDateRange: resetRange,
    }));
  };

  const handleApplyFilters = () => {
    const trimmedAgent = agentFilterInput.trim();
    const trimmedAgentName = agentNameFilterInput.trim();
    const trimmedCustomer = customerFilterInput.trim();
    const trimmedState = stateFilterInput.trim();
    const trimmedSummary = summaryFilterInput.trim();
    const trimmedTopic = topicFilterInput.trim();
    const trimmedScore = scoreFilterInput.trim();
    const normalizedScore = trimmedScore ? Number(trimmedScore) : null;
    const normalizedDurationMin = parseDurationToSeconds(durationMinFilterInput);
    const normalizedDurationMax = parseDurationToSeconds(durationMaxFilterInput);
    const shouldIgnoreDurationRange =
      normalizedDurationMin !== null &&
      normalizedDurationMax !== null &&
      normalizedDurationMin > normalizedDurationMax;
    const resolvedDurationMin = shouldIgnoreDurationRange ? null : normalizedDurationMin;
    const resolvedDurationMax = shouldIgnoreDurationRange ? null : normalizedDurationMax;

    setActiveDateRange(dateRange);
    setActiveAgentFilter(trimmedAgent);
    setActiveAgentNameFilter(trimmedAgentName);
    setActiveCustomerFilter(trimmedCustomer);
    setActiveStateFilter(trimmedState);
    setActiveSummaryFilter(trimmedSummary);
    setActiveTopicFilter(trimmedTopic);
    setActiveScoreFilter(Number.isNaN(normalizedScore) ? null : normalizedScore);
    setActiveDurationMinFilter(resolvedDurationMin);
    setActiveDurationMaxFilter(resolvedDurationMax);
    setStoredFilters({
      dateRange,
      activeDateRange: dateRange,
      agentFilterInput,
      agentNameFilterInput,
      customerFilterInput,
      stateFilterInput,
      summaryFilterInput,
      topicFilterInput,
      scoreFilterInput,
      durationMinFilterInput,
      durationMaxFilterInput,
      activeAgentFilter: trimmedAgent,
      activeAgentNameFilter: trimmedAgentName,
      activeCustomerFilter: trimmedCustomer,
      activeStateFilter: trimmedState,
      activeSummaryFilter: trimmedSummary,
      activeTopicFilter: trimmedTopic,
      activeScoreFilter: Number.isNaN(normalizedScore) ? null : normalizedScore,
      activeDurationMinFilter: resolvedDurationMin,
      activeDurationMaxFilter: resolvedDurationMax,
    });
  };

  const handleClearFilters = () => {
    const clearedFilters = buildDefaultFilters();
    setDateRange(clearedFilters.dateRange);
    setActiveDateRange(clearedFilters.activeDateRange);
    setAgentFilterInput(clearedFilters.agentFilterInput);
    setAgentNameFilterInput(clearedFilters.agentNameFilterInput);
    setCustomerFilterInput(clearedFilters.customerFilterInput);
    setStateFilterInput(clearedFilters.stateFilterInput);
    setSummaryFilterInput(clearedFilters.summaryFilterInput);
    setTopicFilterInput(clearedFilters.topicFilterInput);
    setScoreFilterInput(clearedFilters.scoreFilterInput);
    setDurationMinFilterInput(clearedFilters.durationMinFilterInput);
    setDurationMaxFilterInput(clearedFilters.durationMaxFilterInput);
    setActiveAgentFilter(clearedFilters.activeAgentFilter);
    setActiveAgentNameFilter(clearedFilters.activeAgentNameFilter);
    setActiveCustomerFilter(clearedFilters.activeCustomerFilter);
    setActiveStateFilter(clearedFilters.activeStateFilter);
    setActiveSummaryFilter(clearedFilters.activeSummaryFilter);
    setActiveTopicFilter(clearedFilters.activeTopicFilter);
    setActiveScoreFilter(clearedFilters.activeScoreFilter);
    setActiveDurationMinFilter(clearedFilters.activeDurationMinFilter);
    setActiveDurationMaxFilter(clearedFilters.activeDurationMaxFilter);
    setStoredFilters(clearedFilters);
  };

  const dateRangeFilter = (
    <SpaceBetween size="xs" direction="vertical">
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          gap: "0.5rem",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <label htmlFor="call-date-range-picker" style={{ fontSize: "0.85rem" }}>
            Date
          </label>
          <DateRangePicker
            value={dateRange}
            dateOnly={true}
            onChange={({ detail }) => {
              const nextValue = detail.value;
              if (!nextValue?.startDate && !nextValue?.endDate) {
                handleResetDateRange();
                return;
              }
              const normalizedRange = {
                ...nextValue,
                startDate: normalizeDateOnly(nextValue.startDate),
                endDate: normalizeDateOnly(nextValue.endDate ?? nextValue.startDate),
              };
              setDateRange(normalizedRange);
              setStoredFilters((previous) => ({
                ...(previous || defaultFilters),
                dateRange: normalizedRange,
              }));
            }}
            isValidRange={(value) => validateRange(value)}
            relativeOptions={[]}
            showClearButton={true}
            i18nStrings={{
              todayAriaLabel: "Today",
              nextMonthAriaLabel: "Next month",
              previousMonthAriaLabel: "Previous month",
              startDateLabel: "Start date",
              endDateLabel: "End date",
              startTimeLabel: "Start time",
              endTimeLabel: "End time",
              clearButtonLabel: "Clear and reset to yesterday",
              cancelButtonLabel: "Cancel",
              applyButtonLabel: "Apply",
              formatRelativeRange: () => "",
              formatAbsoluteRange: (value) =>
                `${value.startDate ?? ""} – ${value.endDate ?? ""}`,
              formatRelativeRangeLabel: () => "",
              customRelativeRangeLabel: "Custom range",
              relativeModeTitle: "Relative range",
              absoluteModeTitle: "Absolute range",
              relativeRangeSelectionHeading: "Choose a range",
              singleDateSelectionHeading: "Choose date",
              absoluteRangeSelectionHeading: "Choose a date range",
              dateSelectionHeading: "Choose a date range",
              startDatePlaceholder: "YYYY/MM/DD",
              endDatePlaceholder: "YYYY/MM/DD",
              startTimePlaceholder: "HH:MM",
              endTimePlaceholder: "HH:MM",
              closeButtonAriaLabel: "Close",
              backwardsButtonLabel: "Previous day",
              forwardsButtonLabel: "Next day",
              availableStartDateLabel: "Available start date",
              availableEndDateLabel: "Available end date",
              dateUnavailableAriaLabel: "Date unavailable",
              errorHeading: "Invalid date range",
              relativeRangeOptionLabel: () => "",
              customRelativeRangeOptionLabel: "Custom range",
              renderSelectedRelativeRangeAriaLabel: () => "Selected range",
              renderSelectedAbsoluteRangeAriaLabel: (value) =>
                `Selected range ${value.startDate ?? ""} to ${value.endDate ?? ""}`,
            }}
            id="call-date-range-picker"
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <label htmlFor="agent-filter-input" style={{ fontSize: "0.85rem" }}>
            AGT Ext
          </label>
          <Input
            id="agent-filter-input"
            value={agentFilterInput}
            placeholder="Filter by AGT Ext"
            onChange={({ detail }) => setAgentFilterInput(detail.value)}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <label htmlFor="agent-name-filter-input" style={{ fontSize: "0.85rem" }}>
            AGT Name
          </label>
          <Input
            id="agent-name-filter-input"
            value={agentNameFilterInput}
            placeholder="Filter by AGT Name"
            onChange={({ detail }) => setAgentNameFilterInput(detail.value)}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <label htmlFor="customer-filter-input" style={{ fontSize: "0.85rem" }}>
            Customer
          </label>
          <Input
            id="customer-filter-input"
            value={customerFilterInput}
            placeholder="Filter by customer"
            onChange={({ detail }) => setCustomerFilterInput(detail.value)}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <label htmlFor="state-filter-input" style={{ fontSize: "0.85rem" }}>
            State
          </label>
          <Input
            id="state-filter-input"
            value={stateFilterInput}
            placeholder="Filter by state"
            onChange={({ detail }) => setStateFilterInput(detail.value)}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.25rem",
            width: filterControlWidth,
          }}
        >
          <label htmlFor="summary-filter-input" style={{ fontSize: "0.85rem" }}>
            Summary
          </label>
          <Input
            id="summary-filter-input"
            value={summaryFilterInput}
            placeholder="Filter by summary"
            onChange={({ detail }) => setSummaryFilterInput(detail.value)}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.25rem",
            width: filterControlWidth,
          }}
        >
          <label htmlFor="topic-filter-input" style={{ fontSize: "0.85rem" }}>
            Topic
          </label>
          <Select
            selectedOption={topicOptions.find((option) => option.value === topicFilterInput) || null}
            onChange={({ detail }) =>
              setTopicFilterInput(detail.selectedOption?.value ?? "")
            }
            options={topicOptions}
            placeholder="Select topic"
            selectedAriaLabel="Topic"
            controlId="topic-filter-input"
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <label htmlFor="score-filter-input" style={{ fontSize: "0.85rem" }}>
            Score
          </label>
          <Select
            selectedOption={scoreOptions.find((option) => option.value === scoreFilterInput) || null}
            onChange={({ detail }) =>
              setScoreFilterInput(detail.selectedOption?.value ?? "")
            }
            options={scoreOptions}
            placeholder="Select score"
            selectedAriaLabel="Score"
            controlId="score-filter-input"
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <label style={{ fontSize: "0.85rem" }}>Duration</label>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              <label htmlFor="duration-min-filter-input" style={{ fontSize: "0.75rem" }}>
                Minimum
              </label>
              <Input
                id="duration-min-filter-input"
                value={durationMinFilterInput}
                placeholder="00:00"
                onChange={({ detail }) => setDurationMinFilterInput(detail.value)}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              <label htmlFor="duration-max-filter-input" style={{ fontSize: "0.75rem" }}>
                Maximum
              </label>
              <Input
                id="duration-max-filter-input"
                value={durationMaxFilterInput}
                placeholder="00:00"
                onChange={({ detail }) => setDurationMaxFilterInput(detail.value)}
              />
            </div>
          </div>
        </div>
        <SpaceBetween size="xs" direction="horizontal">
          <Button onClick={handleApplyFilters}>Apply Filter</Button>
          <Button variant="normal" onClick={handleClearFilters}>
            Clear Filter
          </Button>
        </SpaceBetween>
      </div>
      <small>
        Filtering {displayStart}
        {displayEnd && displayEnd !== displayStart ? ` to ${displayEnd}` : ""} (America/Chicago)
        {since && until
          ? ` → UTC ${since} to ${until}`
          : ""}
      </small>
      <Button onClick={handleResetDateRange} variant="link">
        Reset to yesterday
      </Button>
    </SpaceBetween>
  );

  return (
    <>
      <ContentLayout
        header={
          <Header
            variant="h1"
          >
            Call List
          </Header>
        }
      >
        <Container>
          <Grid
            gridDefinition={[
              { colspan: { default: 12 } },
              { colspan: { default: 12 } },
            ]}
          >
            <div style={{ display: "none" }}>
              <ExpandableSection headerText="Upload call recordings">
                <Upload />
              </ExpandableSection>
            </div>
            <SpaceBetween size="s">
              <ContactTable
                data={filteredRecords}
                loading={isTableLoading}
                empty={<Empty />}
                onResolved={() => mutate()}
                username={username}
                extraFilters={dateRangeFilter}
                hideSearchBar={true}
              />
              <small>
                {isTableLoading
                  ? "Loading calls for the selected date range..."
                  : `Loaded ${aggregatedRecords.length} record(s).`}
                {isFetchingAdditionalPages ? " Fetching additional pages..." : ""}
              </small>
            </SpaceBetween>
          </Grid>
        </Container>
      </ContentLayout>
    </>
  );
}

const Empty = () => (
  <div>
    <h2>No results</h2>
  </div>
);

export default Home;
